/**
 * The router.
 *
 * ── WHY THERE IS NO ROUTER LIBRARY HERE ──────────────────────────────────
 * Seven routes, none of them nested, none of them with a data loader. What a
 * router library would add is a dependency, a bundle, and a second mental
 * model for history on a surface that already has one: AdminDashboard writes
 * its entire filter state into the query string with `history.pushState` and
 * reads it back on `popstate`. A library that owns history would either fight
 * that or require it to be rewritten. This file cooperates with it instead,
 * because it keys off the PATHNAME only and ignores query changes entirely.
 *
 * ── DEEP LINKS ───────────────────────────────────────────────────────────
 * `wrangler.toml` sets `not_found_handling = "single-page-application"`, so
 * every unmatched path is served this bundle with HTTP 200. That is what
 * makes `/join/HWARDU` work when a student types it off a poster, and it is
 * also why matching happens here at first render rather than only on click:
 * the first route a visitor gets is almost never `/`.
 *
 * ── THE DELEGATED CLICK HANDLER ──────────────────────────────────────────
 * The five page files were built independently and every internal link in
 * them is a plain `<a href="/checkout?...">`. Rather than edit five files to
 * import a Link component, one capture-phase listener upgrades those anchors
 * to client navigation.
 *
 * It upgrades an anchor ONLY when the destination matches a route this file
 * knows. That single condition is what keeps the real server documents the
 * trust page links to working: `/documents/p3-w9-2026.pdf` and
 * `/legal/enterprise-terms/2026-1` do not match a route, so they fall through
 * to a real navigation and the Worker serves them. An interceptor that
 * swallowed every same-origin href would turn each of those into a blank
 * client-side 404, and nothing would report it.
 */

import { useCallback, useEffect, useSyncExternalStore } from "react";

/* ==========================================================================
   The route table
   ========================================================================== */

export type Route =
  | { readonly kind: "partner" }
  | { readonly kind: "pricing" }
  | { readonly kind: "checkout" }
  | { readonly kind: "checkout-complete" }
  | { readonly kind: "trust" }
  | { readonly kind: "login" }
  | { readonly kind: "admin" }
  | { readonly kind: "demo" }
  | { readonly kind: "admin-settings" }
  | { readonly kind: "demo-settings" }
  | { readonly kind: "admin-billing" }
  | { readonly kind: "demo-billing" }
  | { readonly kind: "admin-team" }
  | { readonly kind: "demo-team" }
  | { readonly kind: "admin-roster" }
  | { readonly kind: "demo-roster" }
  | { readonly kind: "admin-invitations" }
  | { readonly kind: "demo-invitations" }
  | { readonly kind: "admin-matching" }
  | { readonly kind: "demo-matching" }
  | { readonly kind: "join"; readonly code: string }
  | { readonly kind: "join-done" }
  | { readonly kind: "not-found"; readonly pathname: string };

/** Named for the not-found screen, which owes the reader a way out. */
export const KNOWN_PATHS = [
  { path: "/overview", label: "Overview" },
  { path: "/pricing", label: "Registration" },
  { path: "/security", label: "Security" },
  { path: "/checkout", label: "Register" },
  { path: "/login", label: "Login" },
  { path: "/admin", label: "The institutional dashboard" },
  { path: "/demo", label: "A sample institution's dashboard" },
] as const;

function segmentsOf(pathname: string): readonly string[] {
  return pathname.split("/").filter((part) => part !== "");
}

/**
 * `/join/done` and `/join/:code` cannot collide: "DONE" contains an O, and the
 * join alphabet excludes both members of every confusion pair, so no real code
 * can ever spell it. The explicit check is still first, because relying on an
 * alphabet decision made in another file to disambiguate a route is the kind
 * of coupling that survives right up until someone widens the alphabet.
 */
/**
 * Paths that moved. `/partner` became `/overview` on Sep 9 2026 (Thomas: the
 * nav has said Overview since launch, and the address should say the same).
 * The old path still resolves, to the same page, and App rewrites the address
 * bar to the new one so a copied link is the current link.
 */
export const MOVED_PATHS: Readonly<Record<string, string>> = {
  "/partner": "/overview",
  "/trust": "/security",
};

export function matchRoute(pathname: string): Route {
  const parts = segmentsOf(pathname);

  if (parts.length === 0) return { kind: "pricing" };

  const head = parts[0] ?? "";

  if (parts.length === 1) {
    if (head === "overview" || head === "partner") return { kind: "partner" };
    if (head === "pricing") return { kind: "pricing" };
    if (head === "checkout") return { kind: "checkout" };
    /* /security since Sep 9 2026 (Thomas: the page has said Security since
       Sep 8, and the address should say the same). /trust still resolves and
       rewrites itself to /security through MOVED_PATHS. */
    if (head === "security" || head === "trust") return { kind: "trust" };
    /* The administrator's sign-in (Sep 9 2026): a work email, then the
       emailed code, then /admin. Before this the gateway's Login left for
       the student platform, which has no idea what an institution is. */
    if (head === "login") return { kind: "login" };
    if (head === "admin") return { kind: "admin" };
    /* /demo is the signed-out shop window: the same dashboard, the same
       fixtures, but reachable without a session so the team and a prospect
       can be sent a link. It is a SEPARATE route rather than an alias of
       /admin because it renders a banner saying what it is, and because the
       day /admin grows a real session guard, /demo must not grow one with it. */
    if (head === "demo") return { kind: "demo" };
    // A bare /join has no code in it. Join renders its own entry state for
    // that, which is the right screen for a student who typed the domain off
    // a poster and stopped.
    if (head === "join") return { kind: "join", code: "" };
  }

  /* Where Stripe returns a buyer who paid (Sep 14 2026): the Worker's
     success address is /checkout/complete?session_id=<id>. Exactly two
     segments, so /checkout itself is untouched and /checkout/anything else
     is still not found. */
  if (parts.length === 2 && head === "checkout" && parts[1] === "complete") {
    return { kind: "checkout-complete" };
  }

  /* Settings is the dashboard's second screen, on both hosts: the signed-in
     one and the sample one, for the same reason /demo exists at all. */
  if (parts.length === 2 && parts[1] === "settings") {
    if (head === "admin") return { kind: "admin-settings" };
    if (head === "demo") return { kind: "demo-settings" };
  }
  /* Billing is the third screen (Sep 9 2026): the plan, the renewal control
     and the invoices, on both hosts for the same reason Settings is. */
  if (parts.length === 2 && parts[1] === "billing") {
    if (head === "admin") return { kind: "admin-billing" };
    if (head === "demo") return { kind: "demo-billing" };
  }
  if (parts.length === 2 && parts[1] === "team") {
    if (head === "admin") return { kind: "admin-team" };
    if (head === "demo") return { kind: "demo-team" };
  }
  /* Enrollment (Sep 17 2026): the roster of who has joined, by first name
     and last initial, on both hosts for the same reason Settings is. */
  if (parts.length === 2 && parts[1] === "enrollment") {
    if (head === "admin") return { kind: "admin-roster" };
    if (head === "demo") return { kind: "demo-roster" };
  }
  /* Invitations (Sep 17 2026, Thomas): the join link, the code and the
     message to send, off the scrolling dashboard and on its own screen. */
  if (parts.length === 2 && parts[1] === "invitations") {
    if (head === "admin") return { kind: "admin-invitations" };
    if (head === "demo") return { kind: "demo-invitations" };
  }
  /* Matching (Sep 17 2026): a mentor for a student, asked from the portal. */
  if (parts.length === 2 && parts[1] === "matching") {
    if (head === "admin") return { kind: "admin-matching" };
    if (head === "demo") return { kind: "demo-matching" };
  }

  if (head === "join" && parts.length === 2) {
    const tail = parts[1] ?? "";
    if (tail.toLowerCase() === "done") return { kind: "join-done" };
    return { kind: "join", code: safeDecode(tail) };
  }

  return { kind: "not-found", pathname };
}

function safeDecode(value: string): string {
  try {
    return decodeURIComponent(value);
  } catch {
    // A malformed percent-escape is a mistyped link, not a crash. Handing the
    // raw text on lets the join screen say which character it could not read.
    return value;
  }
}

/* ==========================================================================
   History

   One event name covers both directions: the browser's own `popstate` for
   back and forward, and a dispatched event for a programmatic navigate, which
   pushState deliberately does not fire.
   ========================================================================== */

const NAVIGATION_EVENT = "p3e:navigation";

function currentHref(): string {
  if (typeof window === "undefined") return "/";
  const { pathname, search, hash } = window.location;
  return `${pathname}${search}${hash}`;
}

function subscribeToHistory(onChange: () => void): () => void {
  if (typeof window === "undefined") return () => undefined;
  window.addEventListener("popstate", onChange);
  window.addEventListener(NAVIGATION_EVENT, onChange);
  return () => {
    window.removeEventListener("popstate", onChange);
    window.removeEventListener(NAVIGATION_EVENT, onChange);
  };
}

/** The full location as one string, so the store snapshot compares by value. */
export function useHref(): string {
  return useSyncExternalStore(
    subscribeToHistory,
    currentHref,
    () => "/",
  );
}

/** The matched route for the current location. */
export function useRoute(): Route {
  const href = useHref();
  return matchRoute(pathnameOf(href));
}

function pathnameOf(href: string): string {
  const queryAt = href.search(/[?#]/);
  return queryAt === -1 ? href : href.slice(0, queryAt);
}

export interface NavigateOptions {
  readonly replace?: boolean;
}

export function navigate(to: string, options: NavigateOptions = {}): void {
  if (typeof window === "undefined") return;

  const target = new URL(to, window.location.href);
  if (target.origin !== window.location.origin) {
    window.location.assign(target.href);
    return;
  }

  const next = `${target.pathname}${target.search}${target.hash}`;
  if (options.replace) window.history.replaceState(null, "", next);
  else window.history.pushState(null, "", next);
  window.dispatchEvent(new Event(NAVIGATION_EVENT));
}

/** For the handful of places in this file's own JSX that need a link. */
export function useNavigate(): (to: string, options?: NavigateOptions) => void {
  return useCallback((to: string, options?: NavigateOptions) => {
    navigate(to, options);
  }, []);
}

/* ==========================================================================
   Link interception
   ========================================================================== */

function anchorFrom(target: EventTarget | null): HTMLAnchorElement | null {
  if (!(target instanceof Element)) return null;
  const found = target.closest("a");
  return found instanceof HTMLAnchorElement ? found : null;
}

function shouldIntercept(event: MouseEvent, anchor: HTMLAnchorElement): boolean {
  if (event.defaultPrevented) return false;
  if (event.button !== 0) return false;
  if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return false;

  if (anchor.hasAttribute("download")) return false;
  const target = anchor.getAttribute("target");
  if (target !== null && target !== "" && target !== "_self") return false;
  if (anchor.getAttribute("rel")?.split(/\s+/).includes("external")) return false;

  const href = anchor.getAttribute("href");
  if (href === null || href === "") return false;
  // An in-page anchor is the browser's job, and hijacking it breaks the skip
  // link on every one of these pages.
  if (href.startsWith("#")) return false;

  const url = new URL(anchor.href, window.location.href);
  if (url.origin !== window.location.origin) return false;

  return matchRoute(url.pathname).kind !== "not-found";
}

/**
 * Installed once by App. Capture phase, so a page that stops propagation on
 * its own container does not silently disable client navigation for the links
 * inside it.
 */
export function useLinkInterception(): void {
  useEffect(() => {
    if (typeof document === "undefined") return;

    const onClick = (event: MouseEvent): void => {
      const anchor = anchorFrom(event.target);
      if (anchor === null) return;
      if (!shouldIntercept(event, anchor)) return;
      event.preventDefault();
      navigate(anchor.href);
    };

    document.addEventListener("click", onClick, { capture: true });
    return () => document.removeEventListener("click", onClick, { capture: true });
  }, []);
}

/**
 * Scroll handling for client navigation.
 *
 * A page change starts at the top; a query-string change does not, because on
 * the dashboard every filter writes to the query string and yanking the reader
 * back to the header each time they change the window is disorienting. A hash
 * scrolls to its target, which is what the skip links and the section links in
 * Data and methods depend on.
 */
export function useScrollRestoration(route: Route): void {
  const kind = route.kind;

  useEffect(() => {
    if (typeof window === "undefined") return;
    const hash = window.location.hash.slice(1);
    if (hash === "") {
      window.scrollTo(0, 0);
      return;
    }
    const target = document.getElementById(hash);
    if (target === null) {
      window.scrollTo(0, 0);
      return;
    }
    target.scrollIntoView({ block: "start" });
  }, [kind]);
}
